import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { authUtil } from 'src/utils';

export enum ProxyEventTypes {
  SIGN_OUT = 'SIGN_OUT',
  UPDATE_AUTH_TOKEN = 'UPDATE_AUTH_TOKEN',
  UPDATE_NATIVE_SESSION = 'UPDATE_NATIVE_SESSION',
  WEB_APP_READY = 'WEB_APP_READY',
  RECEIVED_MESSAGE = 'RECEIVED_MESSAGE',
  USER_LOGGED_IN = 'USER_LOGGED_IN',
  MESSAGE = 'MESSAGE',
  OPEN_EXTERNAL_URL = 'OPEN_EXTERNAL_URL',
  AUDIO_READY = 'AUDIO_READY',
  AUDIO_STATE = 'AUDIO_STATE',
  AUDIO_ENDED = 'AUDIO_ENDED',
  AUDIO_ERROR = 'AUDIO_ERROR',
  CONFIGURE_PURCHASES = 'CONFIGURE_PURCHASES',
  PURCHASE_PRODUCT = 'PURCHASE_PRODUCT',
  RESTORE_PURCHASES = 'RESTORE_PURCHASES',
  MANAGE_SUBSCRIPTIONS = 'MANAGE_SUBSCRIPTIONS',
  PURCHASE_CAPABILITIES = 'PURCHASE_CAPABILITIES',
  PURCHASE_RESULT = 'PURCHASE_RESULT',
}

const CUSTOM_AUTH_TOKEN_KEY = 'customAuthToken';
const SHOW_PAYMENT_INFO_KEY = 'showPaymentInfo';
const IS_WRAPPED_IN_HOST_APP_KEY = 'isWrapped';

interface ProxyHostMessage {
  type: string;
  data?: any;
}

interface Settings {
  customAuthToken?: string;
  nativeSessionAvailable?: boolean;
  showPaymentInfo?: boolean;
  isWrapped?: boolean;
  nativeAudioEvent?: ProxyHostMessage;
  purchaseMode?: 'web_paddle' | 'ios_iap' | 'native_purchase_unavailable';
  nativeProducts?: Array<{ productId: string; priceString?: string }>;
  nativePurchaseEvent?: ProxyHostMessage;
}

interface HostAppProxyContextType {
  settings: Settings;
  sendMessageToHost: (message: ProxyHostMessage) => void;
  signOut: () => Promise<void>;
  purchaseProduct: (productId: string) => void;
  restorePurchases: () => void;
  manageSubscriptions: () => void;
}

// Create a context with an empty default value
const HostAppProxyContext = createContext<HostAppProxyContextType | undefined>(
  undefined
);

interface ProxyProviderProps {
  children: ReactNode;
}

const isHostedByNativeApp = () =>
  typeof window !== 'undefined' && Boolean(window.ReactNativeWebView);

export const HostAppProxyProvider = ({ children }: ProxyProviderProps) => {
  const [settings, setSettings] = useState<Settings>(() => ({
    isWrapped: isHostedByNativeApp(),
  }));
  const handledMessageEventsRef = useRef<WeakSet<object>>(new WeakSet());
  const nativeAudioReadyDataRef = useRef<any>(null);
  const purchasesConfiguredRef = useRef(false);
  const nativePurchaseBridgeTokenRef = useRef<string | undefined>(undefined);

  const reconcileApplePurchases = async () => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/billing/apple/sync`,
        {
          method: 'POST',
          headers: { 'br-token': await authUtil.getFreshIdToken() },
        }
      );
    } catch {
      // The native purchase has already completed. Webhook retries and the
      // scheduled reconciliation remain authoritative if this eager sync is
      // temporarily unavailable.
    }
  };

  // Handler to receive messages from the host
  const handleMessage = (event: MessageEvent) => {
    if (handledMessageEventsRef.current.has(event)) return;
    handledMessageEventsRef.current.add(event);

    try {
      // Attempt to parse the message data
      let messageData = event.data;
      if (typeof messageData === 'string') {
        try {
          messageData = JSON.parse(messageData);
        } catch (e) {
          // Not JSON, ignore this message
          return;
        }
      }

      // Check if the message is intended for HostAppProxyProvider
      if (
        messageData &&
        messageData.type &&
        Object.values(ProxyEventTypes).includes(messageData.type)
      ) {
        // sendMessageToHost({ type: 'RECEIVED_MESSAGE', data: messageData });

        switch (messageData.type) {
          case ProxyEventTypes.UPDATE_AUTH_TOKEN:
            sendMessageToHost({
              type: ProxyEventTypes.MESSAGE,
              data: 'Received new auth token',
            });
            localStorage.setItem(
              CUSTOM_AUTH_TOKEN_KEY,
              messageData.data?.customAuthToken
            );
            setSettings(previousSettings => ({
              ...previousSettings,
              customAuthToken: messageData.data?.customAuthToken,
              isWrapped: true,
            }));
            break;
          case ProxyEventTypes.UPDATE_NATIVE_SESSION:
            setSettings(previousSettings => ({
              ...previousSettings,
              nativeSessionAvailable: Boolean(
                messageData.data?.nativeSessionAvailable
              ),
              isWrapped: true,
            }));
            break;
          case ProxyEventTypes.AUDIO_READY:
            // React may batch Android's AUDIO_READY immediately followed by
            // AUDIO_STATE into one render. Retain the metadata separately and
            // fold it into later state events so timing/voice cannot disappear
            // while the frequently changing position remains the latest event.
            nativeAudioReadyDataRef.current = messageData.data || null;
            setSettings(previousSettings => ({
              ...previousSettings,
              nativeAudioEvent: messageData,
              isWrapped: true,
            }));
            break;
          case ProxyEventTypes.AUDIO_STATE: {
            const readyData = nativeAudioReadyDataRef.current;
            const stateEvent =
              readyData?.requestId &&
              readyData.requestId === messageData.data?.requestId
                ? {
                    ...messageData,
                    data: { ...readyData, ...messageData.data },
                  }
                : messageData;
            setSettings(previousSettings => ({
              ...previousSettings,
              nativeAudioEvent: stateEvent,
              isWrapped: true,
            }));
            break;
          }
          case ProxyEventTypes.AUDIO_ENDED:
          case ProxyEventTypes.AUDIO_ERROR:
            if (
              messageData.type === ProxyEventTypes.AUDIO_ERROR &&
              nativeAudioReadyDataRef.current?.requestId ===
                messageData.data?.requestId
            ) {
              nativeAudioReadyDataRef.current = null;
            }
            setSettings(previousSettings => ({
              ...previousSettings,
              nativeAudioEvent: messageData,
              isWrapped: true,
            }));
            break;
          case ProxyEventTypes.PURCHASE_CAPABILITIES:
            nativePurchaseBridgeTokenRef.current =
              messageData.data?.bridgeToken;
            setSettings(previousSettings => ({
              ...previousSettings,
              isWrapped: true,
              purchaseMode: messageData.data?.mode,
              nativeProducts: messageData.data?.products || [],
            }));
            if (
              messageData.data?.mode === 'ios_iap' &&
              !purchasesConfiguredRef.current
            ) {
              purchasesConfiguredRef.current = true;
              void configurePurchases();
            }
            break;
          case ProxyEventTypes.PURCHASE_RESULT:
            if (messageData.data?.status === 'success') {
              void reconcileApplePurchases();
            }
            setSettings(previousSettings => ({
              ...previousSettings,
              nativePurchaseEvent: messageData,
              isWrapped: true,
            }));
            break;
          default:
            console.log('Unknown message type:', messageData.type);
            break;
        }
      }
    } catch (error) {
      // Silently ignore any errors
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    // react-native-webview dispatches native postMessage replies on document
    // on Android and on window on iOS. Listen on both; the WeakSet above also
    // prevents a bubbling event from being processed twice.
    document.addEventListener('message', handleMessage as EventListener);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage as EventListener);
    };
  }, []);

  useEffect(() => {
    sendMessageToHost({
      type: ProxyEventTypes.MESSAGE,
      data: 'custom auth token changed',
    });
  }, [settings.customAuthToken]);

  // Load initial settings from localStorage
  useEffect(() => {
    sendMessageToHost({
      type: ProxyEventTypes.WEB_APP_READY,
    });
    const showPaymentInfoStr = localStorage.getItem(SHOW_PAYMENT_INFO_KEY);
    const isWrappedInHostApp = localStorage.getItem(IS_WRAPPED_IN_HOST_APP_KEY);
    const customAuthToken = localStorage.getItem(CUSTOM_AUTH_TOKEN_KEY);
    let showPaymentInfo = true;
    let isWrapped = isHostedByNativeApp();
    if (showPaymentInfoStr) {
      showPaymentInfo = showPaymentInfoStr === 'true';
    }
    if (isWrappedInHostApp) {
      isWrapped = isWrapped || isWrappedInHostApp === 'true';
    }
    console.log({ settings });

    setSettings(previousSettings => ({
      ...previousSettings,
      showPaymentInfo,
      isWrapped,
      customAuthToken: customAuthToken || undefined,
    }));
  }, []);

  // Function to send messages to the host
  const sendMessageToHost = (message: ProxyHostMessage) => {
    if (window.ReactNativeWebView) {
      const purchaseCommand = [
        ProxyEventTypes.CONFIGURE_PURCHASES,
        ProxyEventTypes.PURCHASE_PRODUCT,
        ProxyEventTypes.RESTORE_PURCHASES,
        ProxyEventTypes.MANAGE_SUBSCRIPTIONS,
      ].includes(message.type as ProxyEventTypes);
      const outbound = purchaseCommand
        ? {
            ...message,
            data: {
              ...message.data,
              bridgeToken: nativePurchaseBridgeTokenRef.current,
            },
          }
        : message;
      window.ReactNativeWebView.postMessage(JSON.stringify(outbound));
    } else {
      // console.log('ReactNativeWebView is not available on the window object');
    }
  };

  const configurePurchases = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/billing/apple/customer`,
        { headers: { 'br-token': await authUtil.getFreshIdToken() } }
      );
      if (!response.ok) throw new Error('Apple billing customer unavailable');
      const { appUserId } = await response.json();
      sendMessageToHost({
        type: ProxyEventTypes.CONFIGURE_PURCHASES,
        data: { version: 1, appUserId },
      });
    } catch {
      purchasesConfiguredRef.current = false;
      setSettings(previousSettings => ({
        ...previousSettings,
        purchaseMode: 'native_purchase_unavailable',
      }));
    }
  };

  const requestId = () =>
    `purchase-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const purchaseProduct = (productId: string) =>
    sendMessageToHost({
      type: ProxyEventTypes.PURCHASE_PRODUCT,
      data: { version: 1, requestId: requestId(), productId },
    });
  const restorePurchases = () =>
    sendMessageToHost({
      type: ProxyEventTypes.RESTORE_PURCHASES,
      data: { version: 1, requestId: requestId() },
    });
  const manageSubscriptions = () =>
    sendMessageToHost({
      type: ProxyEventTypes.MANAGE_SUBSCRIPTIONS,
      data: { version: 1, requestId: requestId() },
    });

  const signOut = async () => {
    if (window.ReactNativeWebView) {
      // clear local storage
      localStorage.removeItem(CUSTOM_AUTH_TOKEN_KEY);
      sendMessageToHost({
        type: ProxyEventTypes.SIGN_OUT,
      });
    } else {
      // console.log('ReactNativeWebView is not available on the window object');
    }
  };

  return (
    <HostAppProxyContext.Provider
      value={{
        settings,
        sendMessageToHost,
        signOut,
        purchaseProduct,
        restorePurchases,
        manageSubscriptions,
      }}
    >
      {children}
    </HostAppProxyContext.Provider>
  );
};

export const withHostAppProxy =
  <P extends object>(
    Component: React.ComponentType<
      P & { hostAppProxy: HostAppProxyContextType }
    >
  ) =>
  (props: P) => (
    <HostAppProxyContext.Consumer>
      {hostAppProxy =>
        hostAppProxy ? (
          <Component {...props} hostAppProxy={hostAppProxy} />
        ) : null
      }
    </HostAppProxyContext.Consumer>
  );

// Custom hook to use the HostAppProxyContext
export const useHostAppProxy = (): HostAppProxyContextType => {
  const context = useContext(HostAppProxyContext);
  if (!context) {
    throw new Error(
      'useHostAppProxy must be used within a HostAppProxyProvider'
    );
  }
  return context;
};
